<template>
  <div class='widget-W0158V0001'>
    <div class="sq-widget-container">
      <div v-if="isDataLoading" class="sq-error">
        <div class="sq-loader"></div>
      </div>
      <div v-else>
        <div v-if="isInError" class="sq-error">
          Der opstod en fejl. Prøv igen senere.
        </div>
        <div
          v-else-if="!questionnaires || questionnaires.length === 0"
          class="sq-empty"
        >
          Der er ingen aktuelle spørgeskemaer
        </div>
        <div v-else>
          <div
            v-for="questionnaire in questionnaires"
            :key="questionnaire.questionnaireId"
            class="sq-container"
          >
            <div class="sq-header">
              <div class="sq-title">
                <b>{{ questionnaire.title }}</b>
                <span
                  v-if="!firstAnswerOrDefault(questionnaire)"
                  class="nct-new-document-mark"
                >
                Ny
              </span>
              </div>
            </div>
            <p class="sq-description">{{ questionnaire.description }}</p>
            <div
              v-for="question in questionnaire.questions"
              :key="question.questionId"
              class="sq-body"
            >
              <div class="sq-questions">
                <div class="sq-question">
                  <p v-if="question.description" class="sq-question-text">
                    {{ question.description }}
                  </p>
                  <template v-if="question.type === 1">
                    <fieldset class="sq-fieldset" :disabled="!!question.answer">
                      <div class="sq-radio-group">
                        <div
                          class="custom-control custom-radio"
                          :class="{ 'checked-is-disabled': !!question.answer }"
                        >
                          <input
                            type="radio"
                            class="custom-control-input"
                            :id="'q' + question.questionId + '-yes'"
                            :name="'sq-q' + question.questionId"
                            value="yes"
                            :disabled="isDataSubmitting"
                            v-model="selectedAnswers[question.questionId]"
                          />
                          <label
                            class="custom-control-label"
                            :for="'q' + question.questionId + '-yes'"
                          >
                            {{ question.parsedSettings.yes.label }}
                          </label>
                        </div>
                        <div
                          class="custom-control custom-radio"
                          :class="{ 'checked-is-disabled': !!question.answer }"
                        >
                          <input
                            type="radio"
                            class="custom-control-input"
                            :id="'q' + question.questionId + '-no'"
                            :name="'sq-q' + question.questionId"
                            value="no"
                            :disabled="isDataSubmitting"
                            v-model="selectedAnswers[question.questionId]"
                          />
                          <label
                            class="custom-control-label"
                            :for="'q' + question.questionId + '-no'"
                          >
                            {{ question.parsedSettings.no.label }}
                          </label>
                        </div>
                      </div>
                    </fieldset>
                  </template>
                  <template v-else>
                    <p class="sq-unsupported">
                      Spørgsmålstype {{ question.type }} understøttes ikke endnu.
                    </p>
                  </template>
                </div>
              </div>
            </div>
            <button
              v-if="!firstAnswerOrDefault(questionnaire)"
              type="button"
              class="btn btn-primary sq-confirm-button"
              :disabled="
              canAnswersBeSubmitted(questionnaire) === false || isDataSubmitting
            "
              @click="submitAnswers(questionnaire)"
            >
              <span v-if="isDataSubmitting">Gemmer...</span>
              <span v-else>Gem svar</span>
            </button>
            <div class="sq-info">
              <p
                v-if="firstAnswerOrDefault(questionnaire)"
                class="sq-answered-text"
              >
                Besvaret af
                {{ answeredBy(questionnaire) }} den
                {{ formatDateTime(answeredDate(questionnaire)) }}
              </p>
              <p v-else class="sq-deadline">
                Kan besvares til {{ formatDateTime(questionnaire.end) }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const uriToAulaQuestionnaireApi =
  "https://aulawidgetapi.nordiccloud.dk/api/questionnaire/v1";

export default {
  props: {
    axios: Function,
    getAulaToken: Function,
    triggerStepUp: Function,
    userProfile: String,
    assuranceLevel: Number,
    childFilter: Array,
    institutionFilter: Array,
    notifications: Array,
    deleteNotifications: Function,
  },

  data() {
    return {
      aulaToken: null,
      questionnaires: null,
      selectedAnswers: {},
      isDataLoading: true,
      isDataSubmitting: false,
      isInError: false,
    };
  },

  mounted() {
    this.aulaToken = this.getAulaToken();
    this.$el.closest(".content")?.style.setProperty("border", "none");
  },

  watch: {
    aulaToken() {
      if (this.aulaToken) {
        this.loadData();
      }
    },

    assuranceLevel() {
      this.loadData();
    },
  },

  methods: {
    async loadData() {
      this.isDataLoading = true;
      this.isInError = false;
      try {
        const result = await this.axios.get(
          `${uriToAulaQuestionnaireApi}?userProfile=${this.userProfile}&assuranceLevel=${this.assuranceLevel}&childFilter=${this.childFilter}&institutionFilter=${this.institutionFilter}`,
          {headers: {Authorization: `Bearer ${this.aulaToken}`}},
        );
        const questionnaires = result.data;
        const selectedAnswers = {};

        questionnaires.forEach((questionnaire) => {
          questionnaire.questions = (questionnaire.questions || []).sort(
            (a, b) => a.order - b.order,
          );

          questionnaire.questions.forEach((question) => {
            question.parsedSettings = question.settings
              ? JSON.parse(question.settings)
              : {};

            if (question.answer) {
              question.answer.parsedAnswer = question.answer.answerString
                ? JSON.parse(question.answer.answerString)
                : null;
              selectedAnswers[question.questionId] =
                question.answer.parsedAnswer?.answer ?? null;
            } else {
              selectedAnswers[question.questionId] = null;
            }
          });
        });
        this.questionnaires = questionnaires;
        this.selectedAnswers = selectedAnswers;
        this.synchronizeBadgeNotifications();
      } catch {
        this.isInError = true;
      } finally {
        this.isDataLoading = false;
      }
    },

    async submitAnswers(questionnaire) {
      this.isDataSubmitting = true;
      const answersToSubmit = questionnaire.questions
        .filter((qs) => !qs.answer && this.selectedAnswers[qs.questionId])
        .map((qs) => ({
          questionId: qs.questionId,
          answerString: JSON.stringify({
            answer: this.selectedAnswers[qs.questionId],
          }),
        }));

      try {
        await this.axios.post(uriToAulaQuestionnaireApi, answersToSubmit, {
          headers: {Authorization: `Bearer ${this.aulaToken}`},
        });

        await this.loadData();
      } catch {
        this.isInError = true;
      } finally {
        this.isDataSubmitting = false;
      }
    },

    formatDateTime(dateString) {
      const d = new Date(dateString);
      const datePart = d.toLocaleDateString("da-DK", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
      const timePart = d.toLocaleTimeString("da-DK", {
        hour: "2-digit",
        minute: "2-digit",
      });
      // Non-breaking spaces keep the date and "kl. time" each on one line;
      // the only place the text may wrap is between the two.
      const nbsp = " ";
      return `${datePart.replace(/ /g, nbsp)} kl.${nbsp}${timePart}`;
    },

    firstAnswerOrDefault(questionnaire) {
      return questionnaire.questions?.find((q) => q.answer) ?? null;
    },

    answeredBy(questionnaire) {
      return (
        this.firstAnswerOrDefault(questionnaire)?.answer?.submittedBy ?? ""
      );
    },

    answeredDate(questionnaire) {
      return (
        this.firstAnswerOrDefault(questionnaire)?.answer?.submittedDate ?? null
      );
    },

    canAnswersBeSubmitted(questionnaire) {
      return questionnaire.questions.every(
        (q) => this.selectedAnswers[q.questionId],
      );
    },

    synchronizeBadgeNotifications() {
      try {
        const questionnaireNotAnsweredNotificationIds = this.questionnaires
          .filter((q) => !this.firstAnswerOrDefault(q))
          .map((q) => q.relation?.notificationId)
          .filter((not) => not !== null && not !== undefined);

        const notificationIdsToDelete = this.notifications
          .filter(
            (notification) =>
              !questionnaireNotAnsweredNotificationIds.includes(
                notification.widgetNotificationId,
              ),
          )
          .map((notification) => notification.widgetNotificationId);

        this.deleteNotifications(notificationIdsToDelete);
      } catch {
        return;
      }
    },
  },
};
</script>

<style lang="scss">
.widget-W0158V0001 {
  .sq-widget-container {
    background-color: white;
    min-height: 70px;
    padding: 15px;
  }

  .sq-empty {
    padding: 20px;
    text-align: center;
  }

  .sq-header {
    background-color: white;
    font-size: 24px;
    font-weight: 900;
  }

  .sq-body {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 20px;
  }

  .sq-info {
    flex: 1;
  }

  .sq-questions {
    flex-shrink: 0;
  }

  @media only screen and (max-width: 767px) {
    .sq-body {
      flex-direction: column;
      align-items: stretch;
    }

    .sq-questions {
      align-self: flex-start;
      width: fit-content;
    }
  }

  .sq-question {
    margin: 15px 0;
  }

  .sq-question-text {
    font-weight: bold;
  }

  .sq-fieldset {
    border: none;
    margin-left: 10px;
    padding: 0;
    min-width: 0;
  }

  .sq-fieldset:disabled .custom-radio {
    pointer-events: none;
  }

  .sq-radio-group {
    display: inline-flex;
    flex-direction: column;
    align-items: flex-start;
  }

  .custom-control-input:checked + .custom-control-label {
    font-weight: bold;
  }

  .sq-confirm-button {
    margin-top: 10px;
    margin-bottom: 10px;
  }

  .sq-answered-text,
  .sq-deadline {
    font-size: 0.75rem;
    opacity: 0.7;
  }

  .sq-error {
    position: absolute;
    top: 10px;
    z-index: 10;
    height: 100%;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .sq-loader {
    display: block;
    position: relative;
    margin-left: auto;
    margin-right: auto;
    height: 48px;
    width: 48px;
  }

  .sq-loader::after,
  .sq-loader::before {
    content: "";
    box-sizing: border-box;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    border: 2px solid;
    border-color: black;
    border-color: var(--color-primary-core);
    position: absolute;
    left: 0;
    top: 0;
    animation: sq-animloader 2s linear infinite;
    opacity: 0.1;
  }

  .sq-loader::after {
    animation-delay: 1s;
  }

  .nct-new-document-mark {
    color: #b50050;
    margin-left: 10px;
    font-size: 0.75rem;
  }

  label {
    font-weight: normal;
    color: #19425f;
  }

  @keyframes sq-animloader {
    0% {
      transform: scale(0);
      opacity: 1;
    }
    100% {
      transform: scale(1);
      opacity: 0;
    }
  }
}
</style>
